import {Button, Group, Popover, Stack} from '@mantine/core'
import {DatePicker, type DatesRangeValue, MonthPicker} from '@mantine/dates'
import {IconCalendar, IconChevronDown} from '@tabler/icons-react'
import {useState} from 'react'

import {HeaderSelect} from './header-select'
import classes from './time-navigation.module.css'
import {
    anchorOffsetFromMonthPick,
    boardScaleForViewRange,
    monthPickerValueFromOffset,
    navigationDateLabel,
    normalizePickerDate,
    pickerMinDate,
    weekOffsetFromDate,
    weekRangeFromWeekOffset,
} from './time-navigation-helpers'
import type {ViewRange} from './types'

type TimeNavigationProps = {
    viewRange: ViewRange
    onViewRangeChange: (value: ViewRange) => void
    anchorOffset: number
    onAnchorOffsetChange: (value: number) => void
}

const SCALE_OPTIONS = [
    {value: 'week' as const, label: 'Week'},
    {value: 'month' as const, label: 'Month'},
]

function anchorOffsetFromWeekPickerRange(range: DatesRangeValue): number | null {
    const picked = range[0] ?? range[1]
    if (!picked) {
        return null
    }

    return weekOffsetFromDate(normalizePickerDate(picked))
}

/**
 * Separates *scale* (Week vs. Month) from *position* (which window is in view).
 * Week mode uses Sunday to Saturday calendar weeks.
 */
export function TimeNavigation({
    viewRange,
    onViewRangeChange,
    anchorOffset,
    onAnchorOffsetChange,
}: TimeNavigationProps) {
    const scale = boardScaleForViewRange(viewRange)
    const atOrigin = anchorOffset === 0
    const dateLabel = navigationDateLabel(scale, anchorOffset)
    const weekPickerRange = weekRangeFromWeekOffset(anchorOffset)
    const monthPickerValue = monthPickerValueFromOffset(anchorOffset)
    const minDate = pickerMinDate(scale)
    const [pickerOpen, setPickerOpen] = useState(false)

    function pick(offset: number) {
        onAnchorOffsetChange(offset)
        setPickerOpen(false)
    }

    return (
        <Group gap="sm" wrap="wrap" align="center" maw="100%">
            <HeaderSelect
                aria-label="Board scale"
                value={scale}
                onChange={(value) => {
                    onViewRangeChange(value)
                    onAnchorOffsetChange(0)
                }}
                options={SCALE_OPTIONS}
                width={116}
            />

            <Popover
                width={scale === 'month' ? 280 : 320}
                position="bottom-end"
                opened={pickerOpen}
                onClose={() => setPickerOpen(false)}
            >
                <Popover.Target>
                    <Button
                        onClick={() => setPickerOpen((open) => !open)}
                        maw="100%"
                        aria-label={scale === 'month' ? 'Board month' : 'Board week'}
                        variant="default"
                        justify="space-between"
                        leftSection={<IconCalendar size={16} stroke={1.5} />}
                        rightSection={<IconChevronDown size={16} stroke={1.5} />}
                    >
                        {dateLabel}
                    </Button>
                </Popover.Target>
                <Popover.Dropdown>
                    <Stack gap="xs">
                        {scale === 'month' ? (
                            <MonthPicker
                                aria-label="Select month"
                                value={monthPickerValue}
                                minDate={minDate}
                                onChange={(value) => {
                                    if (value) {
                                        pick(anchorOffsetFromMonthPick(normalizePickerDate(value)))
                                    }
                                }}
                            />
                        ) : (
                            <div className={classes.weekRangePicker}>
                                <DatePicker
                                    type="range"
                                    allowSingleDateInRange
                                    weekendDays={[]}
                                    aria-label="Select week"
                                    value={weekPickerRange}
                                    minDate={minDate}
                                    onChange={(range) => {
                                        const nextOffset = anchorOffsetFromWeekPickerRange(range)
                                        if (nextOffset != null) {
                                            pick(nextOffset)
                                        }
                                    }}
                                />
                            </div>
                        )}
                        <Button
                            variant="default"
                            disabled={atOrigin}
                            onClick={() => pick(0)}
                            fullWidth
                        >
                            {scale === 'month' ? 'This month' : 'This week'}
                        </Button>
                    </Stack>
                </Popover.Dropdown>
            </Popover>
        </Group>
    )
}
